/**
 * SaveFileDialog — Save mode of the host file dialogs.
 *
 * The user picks a folder in their Documents (never Arche Saves or My-Fias),
 * names the file, and settles any name clash — Keep both, or Replace a file
 * they can write. For a plugin's save it also says what the arche keeps
 * afterwards: "can open this file again", optionally "keep editing", or
 * nothing at all when the arche cannot hold that access.
 *
 * The dialog performs the write itself (`saveToDocuments`, the user-session
 * routes). The requesting arche learns only the result — document id, final
 * name and access — never the folder or the path.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FolderPlus, Loader2 } from 'lucide-react';
import {
  api,
  ApiError,
  vaultSaveConflictsOf,
  type VaultContinuedAccess,
  type VaultSaveTargetDto,
  type VaultSaveConflictDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useConsentArmDelay } from '@/lib/hooks/useConsentArmDelay';
import { FileDialogShell } from './FileDialogShell';
import {
  extensionForMime,
  fileNameProblem,
  previewStoredName,
  scrubForDisplay,
} from './display-safety';
import { formatModified, splitName } from './format';
import { describeFolderPath } from './locations';
import { saveToDocuments } from './save-to-documents';
import { useFileBrowser } from './useFileBrowser';

/** A format the caller can produce ("Save as type"). Internal callers only in
 *  v1: a plugin sends bytes of one type. */
export interface SaveFormat {
  label: string;
  /** With the dot: ".png". */
  extension: string;
  mimeType: string;
}

/** Who is saving — decides the header, the arche stamp and the access line. */
export type SaveDialogSaver =
  | {
      kind: 'plugin';
      archeId: string;
      /** Host-fetched display name, never plugin-supplied. */
      archeName: string | null;
      /** The arche may hold read access afterwards (`vault:user-documents:read`). */
      mayKeepReading: boolean;
      /** …and edit access (`vault:user-documents:edit`). */
      mayKeepEditing: boolean;
    }
  | {
      /** An internal arche or host surface saving as the user. */
      kind: 'user';
      /** "Image Editor" — for the header. */
      label?: string;
      /** Last-location memory key (an arche id or surface name). */
      memoryKey: string;
      /**
       * The internal arche whose output this is, recorded as the file's
       * provenance when the dialog uploads the bytes itself (`getBytes`). A
       * `writer` records its arche server-side, from its own route.
       */
      archeId?: string;
    };

export interface SaveFileDialogProps {
  open: boolean;
  /** Called with `false` when the user cancels. Not while a save is running. */
  onOpenChange: (open: boolean) => void;
  saver: SaveDialogSaver;
  /** The name proposed (plugin-supplied names are untrusted). */
  suggestedName: string;
  mimeType: string;
  /** Formats offered (Save as type). Absent or single: the MIME type above. */
  formats?: readonly SaveFormat[];
  /** The acting workspace; `null` = Personal. */
  initialHolderId: string | null;
  /** The bytes to save, produced after the user confirms (and, with
   *  `formats`, in the chosen format). The dialog uploads them on the
   *  user-session Save routes. Give this, or `writer`. */
  getBytes?: (format: SaveFormat | null) => Promise<Blob>;
  /**
   * For bytes an internal arche holds SERVER-side (Banking's transform,
   * Chat's attachment copy, Image Editor's render): the dialog hands its
   * route where the user chose — folder, name, and what to do about a clash
   * — and the route writes through the shared Save-to-Documents service.
   * Refusals come back exactly as on the Save routes (409 `NAME_EXISTS` with
   * `conflicts`, …), so the dialog asks the same questions either way.
   */
  writer?: (
    target: VaultSaveTargetDto,
    format: SaveFormat | null,
  ) => Promise<{ documentId: string; name: string }>;
  onSaved: (result: {
    document: SavedDocument;
    access: VaultContinuedAccess;
    format: SaveFormat | null;
  }) => void;
}

/** What the dialog reports about the file it saved. */
export interface SavedDocument {
  documentId: string;
  name: string;
}

type Step =
  | { kind: 'name' }
  | {
      kind: 'conflict';
      conflicts: VaultSaveConflictDto[];
      /** Why we are asking again (the file changed under a Replace). */
      reason?: string;
    };

export function SaveFileDialog(props: SaveFileDialogProps) {
  const qc = useQueryClient();
  const memoryKey = props.saver.kind === 'plugin' ? props.saver.archeId : props.saver.memoryKey;
  const areas = useMemo(() => ['documents' as const], []);
  const browser = useFileBrowser({
    open: props.open,
    need: 'write',
    areas,
    initialHolderId: props.initialHolderId,
    memoryKey,
  });

  const formats = props.formats?.length ? props.formats : null;
  const [format, setFormat] = useState<SaveFormat | null>(formats?.[0] ?? null);
  const mimeType = format?.mimeType ?? props.mimeType;

  // The name: base editable, extension protected (it follows the type).
  const suggested = useMemo(() => {
    const stored = previewStoredName(props.suggestedName);
    const parts = splitName(stored);
    return {
      base: parts.base || 'Untitled',
      extension: formats?.[0]?.extension ?? (parts.extension || extensionForMime(props.mimeType)),
    };
  }, [props.suggestedName, props.mimeType, formats]);
  const [base, setBase] = useState(suggested.base);
  const [extension, setExtension] = useState(suggested.extension);
  const [editingExtension, setEditingExtension] = useState(false);
  const [extensionWarned, setExtensionWarned] = useState(false);

  const [keepEditing, setKeepEditing] = useState(false);
  const [keepPreviousVersion, setKeepPreviousVersion] = useState(true);
  const [step, setStep] = useState<Step>({ kind: 'name' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState<string | null>(null);
  const [folderError, setFolderError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Fresh state each time the dialog opens.
  useEffect(() => {
    if (!props.open) return;
    setBase(suggested.base);
    setExtension(suggested.extension);
    setEditingExtension(false);
    setExtensionWarned(false);
    setKeepEditing(false);
    setKeepPreviousVersion(true);
    setStep({ kind: 'name' });
    setBusy(false);
    setError(null);
    setNewFolderName(null);
    setFormat(formats?.[0] ?? null);
  }, [props.open, suggested, formats]);

  const armed = useConsentArmDelay(props.open ? `open:${step.kind}` : 'closed');

  const fileName = `${base.trim()}${extension}`;
  const nameProblem = fileNameProblem(fileName);
  const expectedExtension = format?.extension ?? suggested.extension;
  const extensionChanged =
    expectedExtension !== '' && extension.toLowerCase() !== expectedExtension.toLowerCase();

  const location = browser.location;
  const folderId = location.kind === 'folder' ? location.folderId : null;
  const locationLabel = describeFolderPath(
    folderId ? (browser.folders.find((f) => f.folderId === folderId)?.path ?? null) : null,
  );

  const archeLabel =
    props.saver.kind === 'plugin'
      ? scrubForDisplay(props.saver.archeName ?? '', 60) || 'This arche'
      : (props.saver.label ?? null);
  const requestedAccess: VaultContinuedAccess =
    props.saver.kind !== 'plugin' || !props.saver.mayKeepReading
      ? 'none'
      : keepEditing && props.saver.mayKeepEditing
        ? 'write'
        : 'read';

  const finish = useCallback(
    (document: SavedDocument, access: VaultContinuedAccess) => {
      browser.rememberLocation();
      if (browser.holderId) {
        void qc.invalidateQueries({
          queryKey: queryKeys.vaultDocuments.forHolder(browser.holderId),
        });
      }
      void qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.appAccessLists() });
      props.onSaved({ document, access, format });
    },
    [browser, qc, props, format],
  );

  /**
   * `name` is passed, not read from state, where the caller has just chosen
   * it: activating a file sets the name AND saves in the same event, and the
   * state it set is not visible until the next render — reading `fileName`
   * there would save under the previous name and skip the Replace question.
   */
  const runSave = async (
    onConflict: 'fail' | 'keep-both' | 'replace',
    target?: VaultSaveConflictDto,
    name: string = fileName,
  ) => {
    if (!browser.holderId || fileNameProblem(name)) return;
    setBusy(true);
    setError(null);
    try {
      const replace = target
        ? {
            documentId: target.documentId,
            expectedRevision: target.revision,
            keepPreviousVersion,
          }
        : undefined;
      if (props.writer) {
        const saved = await props.writer(
          {
            holderId: browser.holderId,
            folderId,
            name,
            onConflict,
            ...(replace ? { replace } : {}),
          },
          format,
        );
        finish(saved, 'none');
        return;
      }
      if (!props.getBytes) throw new Error('Nothing to save.');
      const bytes = await props.getBytes(format);
      const result = await saveToDocuments({
        holderId: browser.holderId,
        folderId,
        name,
        mimeType,
        bytes,
        ...(props.saver.archeId ? { onBehalfOfArcheId: props.saver.archeId } : {}),
        onConflict,
        ...(replace ? { replace } : {}),
        access: requestedAccess,
      });
      finish(result.document, result.access);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError) {
        if (err.code === 'NAME_EXISTS') {
          const conflicts = vaultSaveConflictsOf(err);
          // Start from what the file's history settings would do, so leaving
          // the box alone never overrides them.
          setKeepPreviousVersion(conflicts[0]?.keepsHistory ?? true);
          setStep({ kind: 'conflict', conflicts });
          return;
        }
        if (err.code === 'REPLACE_TARGET_CHANGED') {
          const fresh = vaultSaveConflictsOf(err);
          if (fresh.length > 0) setKeepPreviousVersion(fresh[0].keepsHistory ?? true);
          setStep(
            fresh.length > 0
              ? { kind: 'conflict', conflicts: fresh, reason: err.message }
              : { kind: 'name' },
          );
          if (fresh.length === 0) setError(err.message);
          return;
        }
        if (err.code === 'REPLACE_TOO_LARGE') {
          setError(err.message);
          return;
        }
        if (err.code === 'STORAGE_QUOTA_EXCEEDED') {
          setError('Your storage is full. Free up space or upgrade your plan, then try again.');
          return;
        }
        if (err.code === 'AREA_READ_ONLY' || err.code === 'VIEWER_CANNOT_WRITE') {
          setError(err.message);
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    }
  };

  const onSaveClick = () => {
    if (nameProblem) {
      setError(nameProblem);
      nameRef.current?.focus();
      return;
    }
    if (extensionChanged && !extensionWarned) {
      setExtensionWarned(true);
      return;
    }
    void runSave('fail');
  };

  const createFolder = async () => {
    const name = (newFolderName ?? '').trim();
    if (!browser.holderId) return;
    const problem = fileNameProblem(name);
    if (problem) {
      setFolderError(problem.replace('file', 'folder'));
      return;
    }
    setFolderError(null);
    try {
      const { folder } = await api.createVaultDocumentFolder({
        holderId: browser.holderId,
        parentFolderId: folderId,
        name,
      });
      await qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.folders(browser.holderId) });
      setNewFolderName(null);
      browser.navigate({ kind: 'folder', area: 'documents', folderId: folder.folderId });
    } catch (err) {
      setFolderError(err instanceof Error ? err.message : 'That folder couldn’t be created.');
    }
  };

  const title =
    props.saver.kind === 'plugin'
      ? `Save from ${archeLabel}`
      : archeLabel
        ? `Save from ${archeLabel}`
        : 'Save to your files';

  const banner =
    newFolderName !== null ? (
      <form
        className="flex flex-wrap items-center gap-2 border-b bg-muted/30 px-2 py-2"
        onSubmit={(e) => {
          e.preventDefault();
          void createFolder();
        }}
      >
        <label htmlFor="file-dialog-new-folder" className="text-sm">
          New folder in {locationLabel}
        </label>
        <Input
          id="file-dialog-new-folder"
          autoFocus
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          className="h-9 w-56"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setNewFolderName(null);
            }
          }}
        />
        <Button type="submit" size="sm">
          Create
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setNewFolderName(null)}>
          Cancel
        </Button>
        {folderError && (
          <p role="alert" className="w-full text-xs text-destructive">
            {folderError}
          </p>
        )}
      </form>
    ) : null;

  const footer =
    step.kind === 'conflict' ? (
      <ConflictPanel
        conflicts={step.conflicts}
        reason={step.reason}
        fileName={fileName}
        mimeType={mimeType}
        locationLabel={locationLabel}
        keepPreviousVersion={keepPreviousVersion}
        onKeepPreviousVersion={setKeepPreviousVersion}
        busy={busy}
        armed={armed}
        error={error}
        onKeepBoth={() => void runSave('keep-both')}
        onReplace={(target) => void runSave('replace', target)}
        onBack={() => {
          setStep({ kind: 'name' });
          setError(null);
        }}
      />
    ) : (
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem] flex-1">
            <label htmlFor="file-dialog-save-name" className="text-xs font-medium">
              File name
            </label>
            <div className="flex items-center rounded-md border focus-within:ring-2 focus-within:ring-ring">
              <input
                id="file-dialog-save-name"
                ref={nameRef}
                className="h-10 min-w-0 flex-1 rounded-l-md bg-transparent px-3 text-sm outline-none"
                value={base}
                autoFocus
                disabled={busy}
                onFocus={(e) => e.currentTarget.select()}
                onChange={(e) => {
                  setBase(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && armed && !busy) {
                    e.preventDefault();
                    onSaveClick();
                  }
                }}
                aria-describedby="file-dialog-save-where"
              />
              {editingExtension ? (
                <input
                  aria-label="Extension"
                  className="h-10 w-20 border-l bg-transparent px-2 text-sm outline-none"
                  value={extension}
                  disabled={busy}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    setExtension(v && !v.startsWith('.') ? `.${v}` : v);
                    setExtensionWarned(false);
                  }}
                />
              ) : (
                extension && (
                  <button
                    type="button"
                    className="h-10 rounded-r-md border-l px-2 text-sm text-muted-foreground hover:text-foreground"
                    title="Change the extension"
                    onClick={() => setEditingExtension(true)}
                  >
                    {extension}
                  </button>
                )
              )}
            </div>
          </div>
          {formats && formats.length > 1 && (
            <div>
              <label htmlFor="file-dialog-save-format" className="text-xs font-medium">
                Save as type
              </label>
              <select
                id="file-dialog-save-format"
                className="h-10 w-full rounded-md border bg-background px-2 text-sm"
                value={format?.extension ?? ''}
                disabled={busy}
                onChange={(e) => {
                  const next = formats.find((f) => f.extension === e.target.value) ?? null;
                  setFormat(next);
                  if (next) setExtension(next.extension);
                  setExtensionWarned(false);
                }}
              >
                {formats.map((f) => (
                  <option key={f.extension} value={f.extension}>
                    {f.label} ({f.extension})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <p id="file-dialog-save-where" className="text-xs text-muted-foreground">
          Saves to <span className="font-medium text-foreground">{locationLabel}</span>
          {browser.workspace && browser.workspaces.length > 1
            ? ` in ${browser.workspace.label}`
            : ''}
        </p>
        {props.saver.kind === 'plugin' && (
          <AccessLine
            archeLabel={archeLabel ?? 'This arche'}
            mayKeepReading={props.saver.mayKeepReading}
            mayKeepEditing={props.saver.mayKeepEditing}
            keepEditing={keepEditing}
            disabled={busy}
            onKeepEditing={setKeepEditing}
          />
        )}
        {extensionChanged && extensionWarned && (
          <p
            role="alert"
            className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            The file is still {mimeType}. Changing its extension to “{extension}” may stop it
            opening correctly. Select Save again to keep this name.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!armed || busy || !browser.holderId} onClick={onSaveClick}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Save
          </Button>
        </div>
      </div>
    );

  return (
    <FileDialogShell
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={title}
      description="Choose a folder in your Documents and a name."
      browser={browser}
      showArcheSaves={false}
      showRecent={false}
      selectedIds={EMPTY}
      multiSelect={false}
      // Choosing an existing file takes its name — the OS-dialog convention.
      onSelectFile={(file) => {
        const parts = splitName(file.name);
        setBase(parts.base);
        if (parts.extension) setExtension(parts.extension);
        setStep({ kind: 'name' });
      }}
      onActivateFile={(file) => {
        const parts = splitName(file.name);
        setBase(parts.base);
        if (parts.extension) setExtension(parts.extension);
        // The name just chosen, explicitly — see `runSave`.
        const chosen = `${parts.base.trim()}${parts.extension || extension}`;
        if (armed && !busy) void runSave('fail', undefined, chosen);
      }}
      toolbarExtra={
        <Button
          variant="outline"
          size="sm"
          className="h-10"
          disabled={busy || browser.isSearching}
          onClick={() => {
            setFolderError(null);
            setNewFolderName('New folder');
          }}
          title="New folder (Ctrl+Shift+N)"
        >
          <FolderPlus className="mr-1.5 h-4 w-4" aria-hidden="true" />
          New folder
        </Button>
      }
      onNewFolder={() => {
        setFolderError(null);
        setNewFolderName('New folder');
      }}
      banner={banner}
      footer={footer}
      busy={busy}
    />
  );
}

const EMPTY: ReadonlySet<string> = new Set();

function AccessLine(props: {
  archeLabel: string;
  mayKeepReading: boolean;
  mayKeepEditing: boolean;
  keepEditing: boolean;
  disabled: boolean;
  onKeepEditing: (next: boolean) => void;
}) {
  if (!props.mayKeepReading) {
    return (
      <p className="text-xs text-muted-foreground">
        {props.archeLabel} won’t be able to open this file after it’s saved.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {props.archeLabel} can open this file again. You can remove that in Arche Access.
      </p>
      {props.mayKeepEditing && (
        <label className="flex items-start gap-2 rounded-md border p-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={props.keepEditing}
            disabled={props.disabled}
            onChange={(e) => props.onKeepEditing(e.target.checked)}
          />
          <span>
            <span className="font-medium">Let {props.archeLabel} keep editing this file</span>
            <span className="block text-xs text-muted-foreground">
              It can save changes without asking each time. Earlier versions are kept unless you’ve
              turned version history off, and you can remove its access in Arche Access.
            </span>
          </span>
        </label>
      )}
    </div>
  );
}

function ConflictPanel(props: {
  conflicts: VaultSaveConflictDto[];
  reason?: string;
  fileName: string;
  mimeType: string;
  locationLabel: string;
  keepPreviousVersion: boolean;
  onKeepPreviousVersion: (next: boolean) => void;
  busy: boolean;
  armed: boolean;
  error: string | null;
  onKeepBoth: () => void;
  onReplace: (target: VaultSaveConflictDto) => void;
  onBack: () => void;
}) {
  // The most recently changed of the files sharing the name is the one a
  // Replace would write into — named explicitly.
  const target = props.conflicts[0];
  const sameType =
    !!target &&
    (target.mimeType ?? 'application/octet-stream').toLowerCase() === props.mimeType.toLowerCase();
  // Only an owner or admin may drop a version the file would keep.
  const historyLocked =
    !!target && target.keepsHistory !== false && target.mayTurnOffHistory === false;
  return (
    <div className="space-y-3" role="group" aria-labelledby="file-dialog-conflict-title">
      <p id="file-dialog-conflict-title" className="text-sm font-medium">
        {props.reason ??
          `A file named “${scrubForDisplay(props.fileName, 120)}” is already in ${props.locationLabel}.`}
      </p>
      {props.conflicts.length > 1 && (
        <p className="text-xs text-muted-foreground">
          {props.conflicts.length} files share this name. Replace would write into the most recently
          changed one.
        </p>
      )}
      {target && (
        <div className="rounded-md border p-3 text-sm">
          <div className="font-medium">{scrubForDisplay(target.name, 120)}</div>
          <div className="text-xs text-muted-foreground">
            {target.sourceArcheName ? `Made by ${target.sourceArcheName}` : 'Your file'} · changed{' '}
            {formatModified(target.updatedAt)}
          </div>
          <div className="mt-2 text-xs">
            {target.arches.length === 0 ? (
              <span className="text-muted-foreground">No arches can open this file.</span>
            ) : (
              <>
                <span className="text-muted-foreground">
                  These arches can open it and will see the new content:
                </span>
                <ul className="mt-1 list-disc pl-5">
                  {target.arches.map((a) => (
                    <li key={`${a.archeId}:${a.via}`}>
                      {a.archeName ?? 'An arche'} — {a.access === 'write' ? 'can edit' : 'can view'}{' '}
                      ({a.via === 'grant' ? 'you gave access' : 'approved for this workspace'})
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          {sameType ? (
            <label className="mt-2 flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={props.keepPreviousVersion}
                disabled={props.busy || historyLocked}
                onChange={(e) => props.onKeepPreviousVersion(e.target.checked)}
              />
              Keep the current version in history
              {historyLocked && (
                <span className="text-muted-foreground">
                  {' '}
                  — only an admin can turn this off here
                </span>
              )}
            </label>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              It’s a different type of file, so it can’t be replaced. Keep both, or choose a
              different name.
            </p>
          )}
          {sameType && !props.keepPreviousVersion && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              The current version won’t be kept.
            </p>
          )}
        </div>
      )}
      {props.error && (
        <p role="alert" className="text-sm text-destructive">
          {props.error}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" disabled={props.busy} onClick={props.onBack}>
          Cancel
        </Button>
        <Button variant="outline" disabled={!props.armed || props.busy} onClick={props.onKeepBoth}>
          Keep both
        </Button>
        {target && sameType && (
          <Button
            variant="destructive"
            disabled={!props.armed || props.busy}
            onClick={() => props.onReplace(target)}
          >
            {props.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Replace
          </Button>
        )}
      </div>
    </div>
  );
}
